import { Box, Flex, Select, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { t } from 'i18next';
import { IOptionExt } from 'interfaces/forms/option-ext';
import { ISelectInput } from 'interfaces/forms/select';
import { ArrayHelper } from 'lib_ts/classes/array.helper';
import { safeNumber } from 'lib_ts/classes/math.utilities';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';

const EMPTY_VALUE = '--EMPTY--';

const MAX_SELECT_OPTIONS = 30;

export const CommonSelectInput = (props: ISelectInput) => {
  const options = useMemo(() => {
    const output: IOptionExt[] = [];

    const grouped = ArrayHelper.groupBy(
      props.options.filter((o) => !o.invisible),
      'group'
    );

    const keys = Object.keys(grouped);

    if (!props.skipSort) {
      keys.sort();

      if (props.reverseSort) {
        keys.reverse();
      }
    }

    keys.forEach((key) => {
      const group = grouped[key];

      if (!group) {
        return;
      }

      if (group.length === 0) {
        return;
      }

      if (key) {
        output.push({
          label: key,
          value: key,
          group: key,
          isGroup: true,
          disabled: true,
          hideControl: true,
        });
      }

      if (!props.skipSort) {
        group.sort((a, b) => StringHelper.safeSort(a.label, b.label));

        if (props.reverseSort) {
          group.reverse();
        }
      }

      output.push(...group);
    });

    return output;
  }, [props.options, props.skipSort, props.reverseSort]);

  useEffect(() => {
    if (!props.suppressWarning && options.length > MAX_SELECT_OPTIONS) {
      NotifyHelper.devWarning({
        message_md: `Select input \`${props.id}\` given too many options (${options.length} > ${MAX_SELECT_OPTIONS}); consider switching to a search input instead.`,
      });
    }
  }, [options]);

  const [key, setKey] = useState(Date.now());
  const [open, setOpen] = useState(false);

  const onValueChange = useCallback(
    (value: string) => {
      if (props.onChange) {
        props.onChange(value);
      }

      if (props.onBooleanChange) {
        props.onBooleanChange([true, 'true'].includes(value));
      }

      if (props.onOptionalBooleanChange) {
        const isEmpty = ['', undefined].includes(value);

        props.onOptionalBooleanChange(
          isEmpty ? undefined : [true, 'true'].includes(value)
        );
      }

      if (props.onNumericChange) {
        const maybeNum = safeNumber(value);
        if (maybeNum !== undefined) {
          props.onNumericChange(maybeNum);
        }
      }

      if (props.onOptionalNumericChange) {
        const maybeNum = safeNumber(value);
        props.onOptionalNumericChange(maybeNum);
      }
    },
    [
      props.onChange,
      props.onBooleanChange,
      props.onOptionalBooleanChange,
      props.onNumericChange,
      props.onOptionalNumericChange,
    ]
  );

  const displayValue = useMemo(() => {
    if (!props.value || props.value === EMPTY_VALUE) {
      return;
    }

    const label = props.options.find(
      // otherwise numbers and booleans would not match
      (o) => `${o.value}` === props.value
    )?.label;

    if (label) {
      return t(label);
    }

    return props.value;
  }, [props.value, props.options]);

  const placeholder = t(
    props.placeholder ?? 'common.select-placeholder'
  ).toString();

  const triggerValue = useMemo(() => {
    if (props.renderDisplayValue) {
      return props.renderDisplayValue(props.value);
    }

    if (displayValue && props.prefixIcon) {
      return (
        <Flex gap="2" align="center" width="100%">
          <Flex align="center">{props.prefixIcon}</Flex>
          <Text truncate>{displayValue || placeholder}</Text>
        </Flex>
      );
    }

    return undefined;
  }, [
    displayValue,
    placeholder,
    props.prefixIcon,
    props.renderDisplayValue,
    props.value,
  ]);

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Select.Root
        key={key}
        data-testid={props.name}
        data-value={props.value}
        size={props.size}
        open={open}
        onOpenChange={(open) => setOpen(open)}
        name={props.name}
        // coalesce to EMPTY_VALUE so the input doesn't get stuck displaying the previous selection
        value={props.value ?? EMPTY_VALUE}
        disabled={props.disabled}
        onValueChange={(v) => {
          const shouldProceed = props.shouldChange
            ? props.shouldChange(v)
            : true;

          if (!shouldProceed) {
            // reset the input to the default value
            setKey(Date.now());
            return;
          }

          // notify parent
          onValueChange(v === EMPTY_VALUE ? '' : v);
        }}
        required={!props.optional}
        autoComplete="off"
      >
        <Select.Trigger
          variant={props.variant}
          color={props.inputColor}
          title={props.title}
          className={props.className}
          disabled={props.disabled}
          placeholder={placeholder}
          style={{ width: '100%' }}
          value={displayValue}
        >
          {triggerValue}
        </Select.Trigger>

        <Select.Content className="BorderedContent" position="popper">
          <Select.Group>
            {props.optional && (
              <Select.Item value={EMPTY_VALUE}>
                <Text
                  style={{
                    color: 'var(--gray-a10)',
                  }}
                  truncate
                >
                  {placeholder}
                </Text>
              </Select.Item>
            )}

            {options
              .filter((o) => !o.invisible)
              .map((option, index) => {
                if (option.disabled) {
                  return (
                    <Select.Label key={index}>
                      <Text mr="1">{t(option.label)}</Text>
                      {option.suffix}
                    </Select.Label>
                  );
                }

                return (
                  <Select.Item
                    key={index}
                    value={option.value}
                    disabled={option.disabled}
                    style={{ height: '100%' }}
                  >
                    <Flex direction="column" pt="2" pb="2">
                      <Flex align="center" gap="2">
                        {option.prefix}
                        {option.menuPrefix && (
                          <Box className="rt-SelectTriggerHidden">
                            {option.menuPrefix}
                          </Box>
                        )}
                        <Text
                          color={option.color}
                          className={option.className}
                          truncate
                        >
                          {t(option.label)}
                        </Text>
                        {option.suffix}
                        {option.menuSuffix && (
                          <Box className="rt-SelectTriggerHidden">
                            {option.menuSuffix}
                          </Box>
                        )}
                      </Flex>

                      {option.description && (
                        <Box className="rt-SelectTriggerHidden">
                          <Text size="1" color={RADIX.COLOR.SECONDARY}>
                            {option.description}
                          </Text>
                        </Box>
                      )}
                    </Flex>
                  </Select.Item>
                );
              })}
          </Select.Group>
        </Select.Content>
      </Select.Root>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};
